import { searchDefaults } from "../../constants";

const { ranges } = searchDefaults;
const meterDefault = "hours";

export const metaInit = {
  frontAxleRating: {
    defaultValues: [ranges.frontAxleRating.min, ranges.frontAxleRating.max],
    max: ranges.frontAxleRating.max,
    min: ranges.frontAxleRating.min,
  },
  grossVehicleWeight: {
    defaultValues: [
      ranges.grossVehicleWeight.min,
      ranges.grossVehicleWeight.max,
    ],
    max: ranges.grossVehicleWeight.max,
    min: ranges.grossVehicleWeight.min,
  },
  horsepower: {
    defaultValues: [ranges.horsepower.min, ranges.horsepower.max],
    max: ranges.horsepower.max,
    min: ranges.horsepower.min,
  },
  meter: {
    defaultValues: [ranges[meterDefault].min, ranges[meterDefault].max],
    max: ranges[meterDefault].max,
    min: ranges[meterDefault].min,
    step: ranges[meterDefault].step,
    type: null,
  },
  price: {
    defaultValues: [ranges.price.min, ranges.price.max],
    max: ranges.price.max,
    min: ranges.price.min,
    step: ranges.price.step,
  },
  rearAxleRating: {
    defaultValues: [ranges.rearAxleRating.min, ranges.rearAxleRating.max],
    max: ranges.rearAxleRating.max,
    min: ranges.rearAxleRating.min,
  },
  sleeperSize: {
    defaultValues: [ranges.sleeperSize.min, ranges.sleeperSize.max],
    max: ranges.sleeperSize.max,
    min: ranges.sleeperSize.min,
  },
  total: 0,
  transmissionSpeed: {
    defaultValues: [ranges.transmissionSpeed.min, ranges.transmissionSpeed.max],
    max: ranges.transmissionSpeed.max,
    min: ranges.transmissionSpeed.min,
  },
  wheelbase: {
    defaultValues: [ranges.wheelbase.min, ranges.wheelbase.max],
    max: ranges.wheelbase.max,
    min: ranges.wheelbase.min,
  },
  year: {
    defaultValues: [ranges.year.min, ranges.year.max],
    max: ranges.year.max,
    min: ranges.year.min,
  },
};

const getRangeValue = (value, type, amount) => {
  value = parseFloat(value);

  if (Number.isNaN(value)) {
    return !type || !ranges[type]
      ? ranges[meterDefault][amount]
      : ranges[type][amount];
  } else {
    if (amount === "max") {
      return !value
        ? !type || !ranges[type]
          ? ranges[meterDefault].max
          : ranges[type].max
        : Math.ceil(value);
    } else {
      return Math.floor(value);
    }
  }
};

export const getRangeLimit = (limit, type, amount, value) => {
  if (!limit && !ranges[type]) {
    return 0;
  }

  let cleanLimit = getRangeValue(limit, type, amount);
  const cleanValue = parseFloat(value);

  if (
    !Number.isNaN(cleanValue) &&
    ((amount === "max" && cleanLimit < cleanValue) ||
      (amount === "min" && cleanLimit > cleanValue))
  ) {
    cleanLimit = cleanValue;
  }

  if (type === "year" && amount === "min" && !cleanLimit) {
    cleanLimit =
      !isNaN(cleanValue) && ranges.year.min > cleanValue
        ? cleanValue
        : ranges.year.min;
  }

  const numberOfSteps =
    amount === "max"
      ? Math.ceil(cleanLimit / ranges[type].step)
      : Math.floor(cleanLimit / ranges[type].step);

  // Don't allow a min. price of zero even if the API asks for it
  if (type === "price" && amount === "min" && (!cleanLimit || !numberOfSteps)) {
    return ranges.price.min;
  } else {
    return numberOfSteps * ranges[type].step;
  }
};

export const getMeta = (filters, listings) => {
  const meta = {};

  meta.frontAxleRating = {
    defaultValues: [
      getRangeValue(
        filters?.minFrontAxleRating ||
          getRangeLimit(
            filters?.sliderLimits?.frontAxleRating?.min,
            "frontAxleRating",
            "min"
          ),
        "frontAxleRating",
        "min"
      ),
      getRangeValue(
        filters?.maxFrontAxleRating ||
          getRangeLimit(
            filters?.sliderLimits?.frontAxleRating?.max,
            "frontAxleRating",
            "max"
          ),
        "frontAxleRating",
        "max"
      ),
    ],
    max: getRangeLimit(
      filters?.sliderLimits?.frontAxleRating?.max,
      "frontAxleRating",
      "max",
      filters?.maxFrontAxleRating
    ),
    min: getRangeLimit(
      filters?.sliderLimits?.frontAxleRating?.min,
      "frontAxleRating",
      "min",
      filters?.minFrontAxleRating
    ),
    step: ranges.frontAxleRating.step,
  };

  meta.grossVehicleWeight = {
    defaultValues: [
      getRangeValue(
        filters?.minGrossVehicleWeight ||
          getRangeLimit(
            filters?.sliderLimits?.grossVehicleWeight?.min,
            "grossVehicleWeight",
            "min"
          ),
        "grossVehicleWeight",
        "min"
      ),
      getRangeValue(
        filters?.maxGrossVehicleWeight ||
          getRangeLimit(
            filters?.sliderLimits?.grossVehicleWeight?.max,
            "grossVehicleWeight",
            "max"
          ),
        "grossVehicleWeight",
        "max"
      ),
    ],
    max: getRangeLimit(
      filters?.sliderLimits?.grossVehicleWeight?.max,
      "grossVehicleWeight",
      "max",
      filters?.maxGrossVehicleWeight
    ),
    min: getRangeLimit(
      filters?.sliderLimits?.grossVehicleWeight?.min,
      "grossVehicleWeight",
      "min",
      filters?.minGrossVehicleWeight
    ),
    step: ranges.grossVehicleWeight.step,
  };

  meta.horsepower = {
    defaultValues: [
      getRangeValue(
        filters?.minHorsepower ||
          getRangeLimit(
            filters?.sliderLimits?.horsepower?.min,
            "horsepower",
            "min"
          ),
        "horsepower",
        "min"
      ),
      getRangeValue(
        filters?.maxHorsepower ||
          getRangeLimit(
            filters?.sliderLimits?.horsepower?.max,
            "horsepower",
            "max"
          ),
        "horsepower",
        "max"
      ),
    ],
    max: getRangeLimit(
      filters?.sliderLimits?.horsepower?.max,
      "horsepower",
      "max",
      filters?.maxHorsepower
    ),
    min: getRangeLimit(
      filters?.sliderLimits?.horsepower?.min,
      "horsepower",
      "min",
      filters?.minHorsepower
    ),
    step: ranges.horsepower.step,
  };

  if (!!filters && !!filters.meterType) {
    const meterRange =
      !!filters.meterType && !!ranges[filters.meterType]
        ? ranges[filters.meterType]
        : ranges[meterDefault];

    meta.meter = {
      defaultValues: [
        getRangeValue(
          filters.minMeter ||
            getRangeLimit(
              filters.sliderLimits.meter.min,
              filters.meterType,
              "min"
            ),
          filters.meterType,
          "min"
        ),
        getRangeValue(
          filters.maxMeter ||
            getRangeLimit(
              filters.sliderLimits.meter.max,
              filters.meterType,
              "max"
            ),
          filters.meterType,
          "max"
        ),
      ],
      max: getRangeLimit(
        filters.sliderLimits.meter.max,
        filters.meterType,
        "max",
        filters.maxMeter
      ),
      min: getRangeLimit(
        filters.sliderLimits.meter.min,
        filters.meterType,
        "min",
        filters.minMeter
      ),
      step: meterRange.step,
      type: filters.meterType || null,
    };
  }

  if (!!filters && !!filters.sliderLimits && !!filters.sliderLimits.price) {
    meta.price = {
      defaultValues: [
        getRangeValue(
          filters.minPrice ||
            getRangeLimit(filters.sliderLimits.price.min, "price", "min"),
          "price",
          "min"
        ),
        getRangeValue(
          filters.maxPrice ||
            getRangeLimit(filters.sliderLimits.price.max, "price", "max"),
          "price",
          "max"
        ),
      ],
      max: getRangeLimit(
        filters.sliderLimits.price.max,
        "price",
        "max",
        filters.maxPrice
      ),
      min: getRangeLimit(
        filters.sliderLimits.price.min,
        "price",
        "min",
        filters.minPrice
      ),
      step: ranges.price.step,
    };
  }

  meta.rearAxleRating = {
    defaultValues: [
      getRangeValue(
        filters?.minRearAxleRating ||
          getRangeLimit(
            filters?.sliderLimits?.rearAxleRating?.min,
            "rearAxleRating",
            "min"
          ),
        "rearAxleRating",
        "min"
      ),
      getRangeValue(
        filters?.maxRearAxleRating ||
          getRangeLimit(
            filters?.sliderLimits?.rearAxleRating?.max,
            "rearAxleRating",
            "rearAxleRating"
          ),
        "rearAxleRating",
        "max"
      ),
    ],
    max: getRangeLimit(
      filters?.sliderLimits?.rearAxleRating?.max,
      "rearAxleRating",
      "max",
      filters?.maxRearAxleRating
    ),
    min: getRangeLimit(
      filters?.sliderLimits?.rearAxleRating?.min,
      "rearAxleRating",
      "min",
      filters?.minRearAxleRating
    ),
    step: ranges.rearAxleRating.step,
  };

  meta.sleeperSize = {
    defaultValues: [
      getRangeValue(
        filters?.minSleeperSize ||
          getRangeLimit(
            filters?.sliderLimits?.sleeperSize?.min,
            "sleeperSize",
            "min"
          ),
        "sleeperSize",
        "min"
      ),
      getRangeValue(
        filters?.maxSleeperSize ||
          getRangeLimit(
            filters?.sliderLimits?.sleeperSize?.max,
            "sleeperSize",
            "max"
          ),
        "sleeperSize",
        "max"
      ),
    ],
    max: getRangeLimit(
      filters?.sliderLimits?.sleeperSize?.max,
      "sleeperSize",
      "max",
      filters?.maxSleeperSize
    ),
    min: getRangeLimit(
      filters?.sliderLimits?.sleeperSize?.min,
      "sleeperSize",
      "min",
      filters?.minSleeperSize
    ),
    step: ranges.sleeperSize.step,
  };

  meta.total = (!!listings && listings.total) || 0;

  meta.transmissionSpeed = {
    defaultValues: [
      getRangeValue(
        filters?.minTransmissionSpeed ||
          getRangeLimit(
            filters?.sliderLimits?.transmissionSpeed?.min,
            "transmissionSpeed",
            "min"
          ),
        "wheelbase",
        "min"
      ),
      getRangeValue(
        filters?.maxTransmissionSpeed ||
          getRangeLimit(
            filters?.sliderLimits?.transmissionSpeed?.max,
            "transmissionSpeed",
            "max"
          ),
        "transmissionSpeed",
        "max"
      ),
    ],
    max: getRangeLimit(
      filters?.sliderLimits?.transmissionSpeed?.max,
      "transmissionSpeed",
      "max",
      filters?.maxTransmissionSpeed
    ),
    min: getRangeLimit(
      filters?.sliderLimits?.transmissionSpeed?.min,
      "transmissionSpeed",
      "min",
      filters?.minTransmissionSpeed
    ),
    step: ranges.transmissionSpeed.step,
  };

  meta.wheelbase = {
    defaultValues: [
      getRangeValue(
        filters?.minWheelbase ||
          getRangeLimit(
            filters?.sliderLimits?.wheelbase?.min,
            "wheelbase",
            "min"
          ),
        "wheelbase",
        "min"
      ),
      getRangeValue(
        filters?.maxWheelbase ||
          getRangeLimit(
            filters?.sliderLimits?.wheelbase?.max,
            "wheelbase",
            "max"
          ),
        "wheelbase",
        "max"
      ),
    ],
    max: getRangeLimit(
      filters?.sliderLimits?.wheelbase?.max,
      "wheelbase",
      "max",
      filters?.maxWheelbase
    ),
    min: getRangeLimit(
      filters?.sliderLimits?.wheelbase?.min,
      "wheelbase",
      "min",
      filters?.minWheelbase
    ),
    step: ranges.wheelbase.step,
  };

  if (!!filters && !!filters.sliderLimits && !!filters.sliderLimits.year) {
    // Arbitrarily offset the mininum year limit if min === max
    if (
      filters.minYear === filters.maxYear &&
      filters.sliderLimits.year.min === filters.sliderLimits.year.max
    ) {
      filters.sliderLimits.year.min = parseInt(
        filters.sliderLimits.year.min,
        10
      );
      if (
        !isNaN(filters.sliderLimits.year.min) &&
        filters.sliderLimits.year.min > ranges.year.min
      ) {
        filters.sliderLimits.year.min = ranges.year.min;
      } else {
        filters.sliderLimits.year.min = filters.sliderLimits.year.min - 5;
      }
    }

    meta.year = {
      defaultValues: [
        getRangeValue(
          filters.minYear ||
            getRangeLimit(filters.sliderLimits.year.min, "year", "min"),
          "year",
          "min"
        ),
        getRangeValue(
          filters.maxYear ||
            getRangeLimit(filters.sliderLimits.year.max, "year", "max"),
          "year",
          "max"
        ),
      ],
      max: getRangeLimit(
        filters.sliderLimits.year.max,
        "year",
        "max",
        filters.maxYear
      ),
      min: getRangeLimit(
        filters.sliderLimits.year.min,
        "year",
        "min",
        filters.minYear
      ),
      step: ranges.year.step,
    };
  }

  // Pagination information
  const {
    current_page = 0,
    last_page = 0,
    per_page = 0,
    from = 0,
    to = 0,
  } = listings || {};
  meta.pagination = {
    current_page,
    last_page,
    per_page: parseInt(per_page, 10),
    from,
    to,
  };

  return meta;
};
